import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, finalize, takeUntil } from 'rxjs';
import { ConfigStateService, LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { buildPhoneNumberPlaceholder } from 'src/core/helpers/phone-number-format.helper';
import { LocalizationDisplayService } from 'src/core/services/settings/localization-display.service';
import { CxSelectComponent } from 'src/app/shared/components/cx-select/cx-select.component';
import { InboundNumberService } from '../../services/inbound-number.service';
import { InboundNumberDto } from '../../models/inbound-number.dto';
import { IvrInstanceLookupDto } from '../../models/ivr-instance-lookup.dto';
import { TelecomProvider } from '../../models/telecom-provider.enum';
import { DIDRoutingType } from '../../models/did-routing-type.enum';
import { PROVIDER_OPTIONS } from '../../models/provider-options.constant';
import { ROUTING_OPTIONS } from '../../models/routing-options.constant';

@Component({
  selector: 'app-inbound-number-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    LocalizationPipe,
    DialogModule,
    InputTextModule,
    CxSelectComponent,
  ],
  templateUrl: './inbound-number-form.component.html',
  styleUrl: './inbound-number-form.component.scss',
})
export class InboundNumberFormComponent implements OnInit, OnChanges, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly inboundNumberService = inject(InboundNumberService);
  private readonly configState = inject(ConfigStateService);
  private readonly localization = inject(LocalizationService);
  private readonly localizationDisplay = inject(LocalizationDisplayService);
  private readonly destroy$ = new Subject<void>();

  private readonly tenantName = (this.configState.getDeep('currentTenant.name') as string) || 'default';

  @Input() visible = false;
  @Input() isEdit = false;
  @Input() model: InboundNumberDto | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<boolean>();

  dialogVisible = false;
  submitting = false;
  ivrInstances: IvrInstanceLookupDto[] = [];

  providerOptions: { value: TelecomProvider; label: string }[] = [];
  routingOptions: { value: DIDRoutingType; label: string; disabled?: boolean }[] = [];

  form: FormGroup = this.fb.group({
    phoneNumber: [
      '',
      [Validators.required, Validators.maxLength(32), Validators.pattern(/^[0-9+]*$/)],
    ],
    provider: [null as TelecomProvider | null, [Validators.required]],
    routingType: [DIDRoutingType.AiAgent, [Validators.required]],
    ivrInstanceId: ['', [Validators.required]],
  });

  /** Example number in the tenant country's international form without spaces, e.g. `+90##########`. */
  get phoneNumberPlaceholder(): string {
    return buildPhoneNumberPlaceholder(this.localizationDisplay.defaultCountryCode).replaceAll(' ', '');
  }

  ngOnInit(): void {
    this.dialogVisible = !!this.visible;
    this.buildLocalizedOptions();
    this.localization.languageChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.buildLocalizedOptions());
    this.inboundNumberService
      .getAssignedIvrInstances()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: list => (this.ivrInstances = list ?? []) });
    this.applyModelToForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildLocalizedOptions(): void {
    this.providerOptions = PROVIDER_OPTIONS.map(o => ({
      value: o.value,
      label: this.localization.instant(o.label),
    }));
    this.routingOptions = ROUTING_OPTIONS.map(o => ({
      value: o.value,
      label: this.localization.instant(o.labelKey),
      disabled: o.disabled,
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      this.dialogVisible = !!this.visible;
    }
    if (changes['model'] || changes['isEdit']) {
      this.applyModelToForm();
    }
  }

  get titleKey(): string {
    return this.isEdit
      ? 'Settings::PhoneNumbers:EditInboundNumber'
      : 'Settings::PhoneNumbers:AddInboundNumber';
  }

  get submitLabelKey(): string {
    return this.isEdit ? 'Settings::PhoneNumbers:Update' : 'Settings::PhoneNumbers:Save';
  }

  get hasIvrInstanceSelected(): boolean {
    return !!this.form.get('ivrInstanceId')?.value;
  }

  get ivrExtensionDisplay(): string {
    if (this.isEdit && this.model?.ivrExtension) {
      return this.model.ivrExtension;
    }
    return `${this.tenantName}_`;
  }

  private applyModelToForm(): void {
    if (!this.form) {
      return;
    }
    if (this.isEdit && this.model) {
      this.form.patchValue({
        phoneNumber: this.model.phoneNumber,
        provider: this.model.provider,
        routingType: this.model.routingType,
        ivrInstanceId: this.model.ivrInstanceId,
      });
    } else {
      this.form.reset({
        phoneNumber: '',
        provider: null,
        routingType: DIDRoutingType.AiAgent,
        ivrInstanceId: '',
      });
    }
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  cancel(): void {
    this.visibleChange.emit(false);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const dto: InboundNumberDto = {
      phoneNumber: (value.phoneNumber as string).trim(),
      provider: value.provider as TelecomProvider,
      routingType: value.routingType as DIDRoutingType,
      ivrInstanceId: value.ivrInstanceId as string,
    } as InboundNumberDto;

    this.submitting = true;

    const editId = this.isEdit ? this.model?.id : undefined;
    const isCreate = editId == null;
    const request$ =
      editId == null
        ? this.inboundNumberService.create(dto)
        : this.inboundNumberService.update({ ...dto, id: editId });

    request$.pipe(finalize(() => (this.submitting = false))).subscribe({
      next: () => this.onSuccess(isCreate),
      error: () => {},
    });
  }

  private onSuccess(isCreate: boolean): void {
    this.submitting = false;
    this.saved.emit(isCreate);
    this.visibleChange.emit(false);
  }
}
